/* global document */

import get from 'lodash/get';
import set from 'lodash/set';
import endsWith from 'lodash/endsWith';
import URL from 'url-parse';

import settings from './settings';

const defaultAppId = '806031192812427';

/**
 * Returns Facebook App ID, null otherwise
 * @return {string}
 */
export function getFacebookAppId() {
  const meta = document.getElementsByName('fb:app_id');

  if (meta.length === 0) {
    return defaultAppId;
  }

  for (let i = 0; i < meta.length; i++) {
    if (meta[i].hasAttribute('content')) {
      return meta[i].getAttribute('content');
    }
  }

  return defaultAppId;
}

/**
 * Returns the resized URL of a poster image.
 * @param {string} originalUrl - original poster image URL.
 * @param {integer} size - new image size.
 * @param {integer} quality - new image quality.
 * @return {string}
 */
export function resizePoster(originalUrl, size = 810, quality = 70) {
  const parsedUrl = new URL(originalUrl, true);

  if (!get(parsedUrl.query, 'resize')) {
    parsedUrl.query = set(parsedUrl.query, 'resize', `${size}:*`);
  }

  if (!get(parsedUrl.query, 'quality')) {
    parsedUrl.query = set(parsedUrl.query, 'quality', `${quality}`);
  }

  const isPNG = endsWith(parsedUrl.pathname, '.png') || endsWith(parsedUrl.pathname, '.PNG');
  if (isPNG && parsedUrl.query.quality) {
    delete parsedUrl.query.quality;
  }

  return parsedUrl.toString();
}

/**
 * Generate a random ID string.
 */
export function generateRandomId() {
  return Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
}

/**
 * Evalutes data attributes set on the element. Supported data attributes are as follows:
 * ```
 * data-autoplay
 * data-muted
 * data-ad-tag
 * data-volume
 * data-controls
 * data-dock
 * data-disable-ads
 * data-aspect-ratio
 * ```
 *
 * @param {HTMLElement} element
 */
export function evaluateDOMSettings(element) {
  const prefix = settings.dataAttributePrefix || 'data';

  const autoplay = element.getAttribute(`${prefix}-autoplay`);
  const muted = element.getAttribute(`${prefix}-muted`);
  const adTag = element.getAttribute(`${prefix}-ad-tag`);
  const volume = element.getAttribute(`${prefix}-volume`);
  const controls = element.getAttribute(`${prefix}-controls`);
  const dock = element.getAttribute(`${prefix}-dock`);
  const aspectRatio = element.getAttribute(`${prefix}-aspect-ratio`);
  const disableAds = element.getAttribute(`${prefix}-disable-ads`);

  return {
    autoplay: autoplay ? autoplay === 'true' || autoplay === '1' : null,
    muted: muted ? muted === 'true' || muted === '1' : null,
    controls: controls ? controls === 'true' || controls === '1' : null,
    dock: dock ? dock === 'true' || dock === '1' : null,

    // special handling, as volume is 1.0 -> 0.0
    volume: volume ? parseFloat(volume) : null,

    // since these are strings, they can be just passed through
    adTag,
    aspectRatio,

    // special case, as we are flipping the conditional here
    ads: disableAds ? disableAds === 'false' || disableAds === '0' : null,
  };
}

/**
 * Evalutes the settings of the embed and the DOM to return what the player should use
 * for trying to set up video.js
 *
 * @param {HTMLElement} element
 * @param {Embed} embed
 */
export function evaluateSettings(element, embed) {
  const {
    autoplay,
    adTag,
    ads,
    muted,
    aspectRatio,

    volume,
    dock,
    controls,
  } = evaluateDOMSettings(element);

  return {
    // combined with the embed
    autoplay: autoplay !== null ? autoplay : embed.autoplay,
    adTag: adTag !== null ? adTag : embed.adTag,
    ads: ads !== null ? ads : embed.adsEnabled,
    muted: muted !== null ? muted : embed.muted,
    aspectRatio: aspectRatio !== null ? aspectRatio : embed.aspectRatio,

    // default for now
    volume: volume !== null ? volume : 1,
    dock: dock !== null ? dock : true,
    controls: controls !== null ? controls : true,
  };
}

/**
 * Check if browser is Edge.
 * @param {Object} navigator - A Navigator instance.
 * @returns {bool}
 */
export function isBrowserEdge(navigator) {
  const ua = navigator.userAgent;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  return false;
}

/**
 * jquery $(document).width() does not take in consideration the scrollbar
 * and gives a different value than the one matched in the media query
 * and this will work faster than matchMedia() api for now
 */
export function calculateViewport() {
  let elem;
  let prefix;
  const hasInnerWidthProperty = Object.prototype.hasOwnProperty.call(window, 'innerWidth');

  if (!hasInnerWidthProperty) {
    prefix = 'client';
    elem = document.documentElement || document.body;
  } else {
    elem = window;
    prefix = 'inner';
  }

  return { width: elem[`${prefix}Width`], height: elem[`${prefix}Height`] };
}



// WEBPACK FOOTER //
// ./src/utils.js